import { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { X, Send, Loader2, ExternalLink, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { createChatClient, getChatSessionKey } from "@/integrations/supabase/bookingClient";
import vibeCharacter from "@/assets/vibe-character.png";

interface ChatAction {
  type: 'link';
  label: string;
  url: string;
}

interface Message {
  role: "user" | "assistant";
  content: string;
  actions?: ChatAction[];
}

const INITIAL_GREETING = "Hi! I'm Vibe, your moving consultant. Where are you moving from?";
const CONVERSATION_STORAGE_KEY = "vibe_widget_conversation_id";

export default function VibeWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: INITIAL_GREETING }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load conversation ID from localStorage on mount
  useEffect(() => {
    const storedId = localStorage.getItem(CONVERSATION_STORAGE_KEY);
    if (storedId) {
      setConversationId(storedId);
    }
  }, []);

  // Auto-scroll to bottom when messages change or loading state changes
  useEffect(() => {
    // Use setTimeout to ensure DOM has updated after React re-render
    setTimeout(() => {
      if (scrollRef.current) {
        const viewport = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          viewport.scrollTop = viewport.scrollHeight;
        }
      }
    }, 0);
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Listen for global "vibe:open" event so pages can proactively open chat
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener("vibe:open", handler);
    return () => window.removeEventListener("vibe:open", handler);
  }, []);

  // Mark user-initiated opens so auto-open hook doesn't re-trigger
  useEffect(() => {
    if (isOpen && typeof window !== "undefined") {
      sessionStorage.setItem("vibe_user_opened", "1");
    }
  }, [isOpen]);

  // Create a new conversation record in the database
  const createConversation = async (firstMessage: string): Promise<string | null> => {
    try {
      const { data, error } = await createChatClient()
        .from("chat_conversations")
        .insert({
          title: firstMessage.slice(0, 50),
          user_id: null,
          is_public: true,
          source: 'vibe_widget',
          status: 'active',
          last_message_preview: firstMessage.slice(0, 100),
          anon_session_key: getChatSessionKey()
        })
        .select()
        .single();
      
      if (error) {
        console.error("Error creating conversation:", error);
        return null;
      }
      
      const newId = data.id;
      setConversationId(newId);
      localStorage.setItem(CONVERSATION_STORAGE_KEY, newId);
      return newId;
    } catch (error) {
      console.error("Error creating conversation:", error);
      return null;
    }
  };

  // Save a message to the database
  const saveMessage = async (convId: string, role: "user" | "assistant", content: string) => {
    try {
      await createChatClient().from("chat_messages").insert({
        conversation_id: convId,
        role,
        content
      });
    } catch (error) {
      console.error("Error saving message:", error);
    }
  };

  // Update conversation preview
  const updateConversationPreview = async (convId: string, preview: string) => {
    try {
      await createChatClient()
        .from("chat_conversations")
        .update({ 
          last_message_preview: preview.slice(0, 100),
          updated_at: new Date().toISOString()
        })
        .eq("id", convId);
    } catch (error) {
      console.error("Error updating conversation preview:", error);
    }
  };

  const sendMessage = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;

    // Add user message to UI immediately
    const userMessage: Message = { role: "user", content: trimmedInput };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    // Create or use existing conversation for persistence
    let currentConvId = conversationId;
    if (!currentConvId) {
      currentConvId = await createConversation(trimmedInput);

      // Alert admins that a real conversation just started (once per conversation)
      supabase.functions
        .invoke("notify-chat-opened", {
          body: {
            page: window.location.pathname,
            conversation_id: currentConvId,
            first_message: trimmedInput,
          },
        })
        .then(({ error }) => {
          if (error) console.error("[VIBE] chat-start notification failed:", error);
        });
    }

    // Save user message to database (async, don't block UI)
    if (currentConvId) {
      saveMessage(currentConvId, "user", trimmedInput);
    }


    try {
      // Build conversation history for context
      const conversationHistory = [...messages, userMessage].map(m => ({
        role: m.role,
        content: m.content
      }));

      const fetchVibeSimple = async (): Promise<Response> => {
        return fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vibe-simple`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ 
              messages: conversationHistory,
              conversationId: currentConvId
            }),
          }
        );
      };

      let response = await fetchVibeSimple();

      // Retry once after 1.5s on failure (handles transient AI gateway issues)
      if (!response.ok) {
        console.warn(`[VIBE] First attempt failed with HTTP ${response.status}, retrying in 1.5s...`);
        await new Promise(resolve => setTimeout(resolve, 1500));
        response = await fetchVibeSimple();
      }

      if (!response.ok) {
        // Parse the error response body for specific messages
        let errorBody = "";
        try {
          const errData = await response.json();
          errorBody = errData.error || errData.message || "";
        } catch { /* ignore parse errors */ }

        console.error(`[VIBE] Edge function error: HTTP ${response.status}`, errorBody);

        // Show specific messages for known error types
        if (response.status === 429) {
          throw new Error(errorBody || "I'm getting a lot of messages right now. Please try again in a moment.");
        } else if (response.status === 403) {
          throw new Error(errorBody || "I'm unable to connect from your location. Please call us at (630) 686-9987.");
        } else {
          throw new Error(errorBody || `Connection error (${response.status})`);
        }
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      const assistantContent = data.response || "I'm having trouble. Please call us at (630) 686-9987.";
      
      // Add message with actions if present
      const newMessage: Message = { role: "assistant", content: assistantContent };
      if (data.actions && Array.isArray(data.actions)) {
        newMessage.actions = data.actions;
      }
      
      setMessages(prev => [...prev, newMessage]);

      // Save assistant message and update preview (async, don't block UI)
      if (currentConvId) {
        saveMessage(currentConvId, "assistant", assistantContent);
        updateConversationPreview(currentConvId, assistantContent);
      }

    } catch (error) {
      console.error("[VIBE] Chat error:", error);
      const errorMessage = error instanceof Error && error.message && !error.message.startsWith("Connection error")
        ? error.message
        : "I'm having trouble connecting. Please try again or call us at (630) 686-9987.";
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: errorMessage },
      ]);
      
      // DON'T persist error messages to DB - they clutter admin chat view
      // and misrepresent VIBE's actual responses
    } finally {
      setIsLoading(false);
      // Refocus input after sending
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    setMessages([{ role: "assistant", content: INITIAL_GREETING }]);
    setInput("");
    // Clear conversation ID to start fresh
    setConversationId(null);
    localStorage.removeItem(CONVERSATION_STORAGE_KEY);
  };

  const formatChatTranscript = (): string => {
    const timestamp = new Date().toLocaleString('en-US', {
      dateStyle: 'full',
      timeStyle: 'short'
    });

    const messagesHtml = messages.map(msg => {
      const isUser = msg.role === 'user';
      const bgColor = isUser ? '#2563eb' : '#f3f4f6';
      const textColor = isUser ? '#ffffff' : '#1f2937';
      const align = isUser ? 'right' : 'left';
      const label = isUser ? 'You' : 'Vibe';
      
      return `
        <div style="text-align: ${align}; margin-bottom: 12px;">
          <span style="font-size: 11px; color: #6b7280; display: block; margin-bottom: 4px;">${label}</span>
          <div style="display: inline-block; max-width: 80%; background-color: ${bgColor}; color: ${textColor}; padding: 10px 14px; border-radius: 12px; text-align: left;">
            ${msg.content}
          </div>
        </div>
      `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 0; background-color: #f9fafb;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background-color: #2563eb; color: white; padding: 20px; border-radius: 12px 12px 0 0; text-align: center;">
            <h1 style="margin: 0; font-size: 20px;">LifeTime Movers</h1>
            <p style="margin: 5px 0 0 0; font-size: 14px; opacity: 0.9;">Chat Session with Vibe</p>
          </div>
          <div style="background-color: white; padding: 20px; border: 1px solid #e5e7eb; border-top: none;">
            <p style="font-size: 12px; color: #6b7280; margin: 0 0 16px 0;">${timestamp}</p>
            ${messagesHtml}
          </div>
          <div style="background-color: #f3f4f6; padding: 16px; border-radius: 0 0 12px 12px; text-align: center; border: 1px solid #e5e7eb; border-top: none;">
            <p style="margin: 0; font-size: 12px; color: #6b7280;">LifeTime Movers | (630) 686-9987 | info@lifetimemovers.us</p>
            <p style="margin: 8px 0 0 0; font-size: 11px; color: #9ca3af;">2021 Midwest Rd STE 200, Oak Brook, IL</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const sendChatTranscript = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSendingEmail(true);

    try {
      const htmlContent = formatChatTranscript();

      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          to: customerEmail.trim(),
          subject: 'Your LifeTime Movers Chat Session with Vibe',
          html: htmlContent
        }
      });

      if (error) throw error;

      toast.success("Chat transcript sent to your email!");
      setEmailDialogOpen(false);
      setCustomerEmail("");
    } catch (error) {
      console.error("Error sending chat transcript:", error);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (!isOpen) {
    return (
      <div
        className="fixed right-4 top-1/2 -translate-y-1/2 z-[9999]"
        style={{
          position: 'fixed',
          right: '1rem',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 9999,
          willChange: 'transform',
        }}
      >
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="h-16 w-16 rounded-full shadow-lg hover:scale-105 transition-transform overflow-hidden border-2 border-primary cursor-pointer"
          style={{
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            minWidth: '64px',
            minHeight: '64px',
          }}
        >
          <img 
            src={vibeCharacter} 
            alt="Vibe" 
            className="h-full w-full object-cover pointer-events-none"
          />
        </button>
      </div>
    );
  }

  return (
    <div 
      className="fixed right-6 top-1/2 -translate-y-1/2 w-[380px] max-w-[calc(100vw-3rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-background border rounded-2xl shadow-2xl flex flex-col z-[9999] overflow-hidden"
      style={{
        position: 'fixed',
        zIndex: 9999,
        willChange: 'transform',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-primary text-primary-foreground">
        <div className="flex items-center gap-3">
          <img 
            src={vibeCharacter} 
            alt="Vibe" 
            className="h-8 w-8 rounded-full object-cover border border-primary-foreground/30"
          />
          <div>
            <h3 className="font-semibold text-sm">Vibe</h3>
            <p className="text-xs opacity-80">Moving Consultant</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEmailDialogOpen(true)}
            className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 text-xs"
            title="Email this chat"
          >
            <Mail className="h-3.5 w-3.5 mr-1" />
            Email
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetChat}
            className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 text-xs"
          >
            New Chat
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={cn(
                "flex",
                msg.role === "user" ? "justify-end" : "justify-start"
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-4 py-2 text-sm",
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-muted text-foreground rounded-bl-md"
                )}
              >
                {msg.content ? (
                  <span dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(
                      msg.content.replace(
                        /(https?:\/\/[^\s]+)/g, 
                        '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary underline hover:text-primary/80 break-all">$1</a>'
                      ),
                      { ALLOWED_TAGS: ['a', 'br', 'strong', 'em', 'b', 'i', 'p', 'ul', 'ol', 'li', 'span'], ALLOWED_ATTR: ['href', 'target', 'rel', 'class'] }
                    )
                  }} />
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {/* Action buttons */}
                {msg.actions && msg.actions.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/50 flex flex-col gap-2">
                    {msg.actions.map((action, idx) => (
                      <a
                        key={idx}
                        href={action.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-xs font-medium hover:bg-primary/90 transition-colors"
                      >
                        {action.label}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {isLoading && messages[messages.length - 1]?.role === "user" && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-3 border-t">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            disabled={isLoading}
            className="flex-1"
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            size="icon"
            className="shrink-0"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Email Chat Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Email Chat Transcript</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Enter your email address to receive a copy of this chat conversation.
            </p>
            <Input
              type="email"
              placeholder="your@email.com"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              disabled={isSendingEmail}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailDialogOpen(false)}
              disabled={isSendingEmail}
            >
              Cancel
            </Button>
            <Button
              onClick={sendChatTranscript}
              disabled={!customerEmail.trim() || isSendingEmail}
            >
              {isSendingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
